import { jwtDecode } from 'jwt-decode';
import fetchWithAuth from './fetchWithAuth';
import { getEndpoints, buildEndpoint } from './ApiContext';

/**
 * Utility functions for admin-specific operations
 * This file contains functions that were previously in Context.jsx
 * to make the code more modular and maintainable
 */

/**
 * Load collections for users
 * @param {string} currentUsername - The admin username
 * @param {Object} apiEndpoints - API endpoints object
 * @param {Object} endpoints - Endpoints from getEndpoints
 * @returns {Promise<Array>} Array of collections
 */
export const fetchUserCollections = async () => {
  try {
    const profileUrl = await buildEndpoint('API_PROFILES');
    const profileResponse = await fetchWithAuth(profileUrl);

    if (!profileResponse.ok) {
      throw new Error(
        `Error al obtener perfil del usuario: ${await profileResponse.text()}`
      );
    }

    const profileData = await profileResponse.json();

    if (
      !profileData ||
      !profileData.backend_config ||
      !profileData.backend_config.collections
    ) {
      throw new Error('El perfil de usuario no tiene colecciones configuradas');
    }

    return {
      collections: profileData.backend_config.collections,
      profileId: profileData.profile_id,
    };
  } catch (error) {
    console.error('Error en fetchUserCollections:', error);
    throw error;
  }
};

/**
 * Verify admin collection access
 * @param {string} collectionName - The collection name to verify
 * @param {Array} collections - Array of available collections
 * @returns {boolean} True if admin has access to the collection
 */
export const verifyAdminCollectionAccess = (collectionName, collections) => {
  return collections.includes(collectionName);
};

/**
 * Verify user association
 * @param {string} username - The username to verify
 * @param {Object} apiEndpoints - API endpoints object
 * @param {Object} endpoints - Endpoints from getEndpoints
 * @returns {Promise<Object>} Association data
 */
export const verifyUserAssociation = async (
  username,
  apiEndpoints,
  endpoints
) => {
  try {
    const API_ASSOCIATION_ITEM = await buildEndpoint('API_ASSOCIATION_ITEM');
    const associationUrl = `${API_ASSOCIATION_ITEM}${username}`;

    const response = await fetchWithAuth(associationUrl);

    if (!response.ok) {
      throw new Error(
        `Error al verificar asociación: ${await response.text()}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error en verifyUserAssociation:', error);
    throw error;
  }
};

/**
 * Check association status
 * @param {string} username - The username to check
 * @param {Object} apiEndpoints - API endpoints object
 * @param {Object} endpoints - Endpoints from getEndpoints
 * @returns {Promise<boolean>} True if user has association
 */
export const checkAssociationStatus = async (
  username,
  apiEndpoints,
  endpoints
) => {
  try {
    await verifyUserAssociation(username, apiEndpoints, endpoints);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Decode user token
 * @param {string} token - The JWT token
 * @returns {Object} Decoded token data
 */
export const decodeUserToken = (token) => {
  if (!token) {
    return {
      username: null,
      email: null,
      groups: [],
    };
  }

  try {
    const decoded = jwtDecode(token);
    return {
      username: decoded.username || decoded.sub,
      email: decoded.email,
      groups: decoded['cognito:groups'] || [],
    };
  } catch (error) {
    console.error('Error al decodificar token:', error);
    return {
      username: null,
      email: null,
      groups: [],
    };
  }
};
